/**
 * Sprint 23.7 — `/welcome/intent` (step 1). Captures the user's intent in a
 * textarea and carries it forward to the theme step via the `?intent=` query
 * param. "Continue" is disabled until there's something to work with; "Skip"
 * marks onboarding done and drops the user on the `/workforce` empty state.
 */

import { useState, type CSSProperties } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { useOnboardingComplete } from "../../hooks/use-onboarding";
import { PrimaryButton, StepHeading, StepLabel, StepSpacer } from "./welcome-ui";

export function WelcomeIntentStep() {
  const navigate = useNavigate();
  const labels = useThemedLabels();
  const [searchParams] = useSearchParams();
  const { markComplete } = useOnboardingComplete();

  const [intent, setIntent] = useState(() => searchParams.get("intent") ?? "");
  const [focused, setFocused] = useState(false);

  const canContinue = intent.trim().length > 0;

  const onContinue = () => {
    if (!canContinue) return;
    navigate(`/welcome/theme?intent=${encodeURIComponent(intent.trim())}`);
  };

  const onSkip = async () => {
    try {
      await markComplete();
    } catch {
      /* best-effort — still let them through */
    }
    navigate("/workforce");
  };

  return (
    <>
      <StepLabel>Step 1 of 3</StepLabel>
      <StepHeading>{labels.onboardingWelcomeTitle}</StepHeading>
      <p style={subParagraph}>{labels.onboardingIntentLabel}</p>

      <textarea
        autoFocus
        value={intent}
        onChange={(e) => setIntent(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={labels.onboardingIntentPlaceholder}
        aria-label="What do you want done?"
        style={{
          ...textareaStyle,
          border: `0.5px solid ${focused ? "var(--color-border-info)" : "var(--color-border-tertiary)"}`,
        }}
      />

      <StepSpacer />

      <div style={footer}>
        <PrimaryButton onClick={onContinue} disabled={!canContinue}>
          Continue
        </PrimaryButton>
        <button type="button" onClick={() => void onSkip()} style={skipLink}>
          Skip — I'll explore first
        </button>
      </div>
    </>
  );
}

const subParagraph: CSSProperties = {
  margin: 0,
  fontSize: 14,
  color: "var(--color-text-secondary)",
  lineHeight: 1.55,
};

const textareaStyle: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: "10px 12px",
  fontSize: 13,
  color: "var(--color-text-primary)",
  lineHeight: 1.5,
  minHeight: 72,
  resize: "vertical",
  outline: "none",
  fontFamily: "var(--font-sans)",
};

const footer: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  alignItems: "center",
};

const skipLink: CSSProperties = {
  background: "transparent",
  border: "none",
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
